import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { useMetaPageView } from './lib/metaEvent';
import { HelmetProvider } from 'react-helmet-async';
import { useShellSeoCleanup } from './useShellSeoCleanup';
import { Toaster } from './components/ui/sonner';
import '@/App.css';
import { AuthProvider, useAuth } from './lib/auth';
import { lazyPage } from './lib/lazyPage';
import AuthCallback from './pages/AuthCallback';
const Login = lazyPage(() => import('./pages/Login'));
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const RateArchiveAudit = lazyPage(() => import('./pages/RateArchiveAudit'));
const ModulesHome = lazyPage(() => import('./pages/ModulesHome'));
const AnalyticsAdmin = lazyPage(() => import('./pages/AnalyticsAdmin'));
const SeoAdmin = lazyPage(() => import('./pages/SeoAdmin'));
const Rates = lazyPage(() => import('./pages/Rates'));
const Promos = lazyPage(() => import('./pages/Promos'));
const Policies = lazyPage(() => import('./pages/Policies'));
const Documents = lazyPage(() => import('./pages/Documents'));
const Buydown = lazyPage(() => import('./pages/Buydown'));
const LenderPicker = lazyPage(() => import('./pages/LenderPicker'));
const LenderProfile = lazyPage(() => import('./pages/LenderProfile'));
const Settings = lazyPage(() => import('./pages/Settings'));
const Search = lazyPage(() => import('./pages/Search'));
const RateHistory = lazyPage(() => import('./pages/RateHistory'));
const Predictor = lazyPage(() => import('./pages/Predictor'));
const PredictorLearning = lazyPage(() => import('./pages/PredictorLearning'));
const FeaturedRatesAdmin = lazyPage(() => import('./pages/FeaturedRatesAdmin'));
const PromoPublicReview = lazyPage(() => import('./pages/PromoPublicReview'));
const Leads = lazyPage(() => import('./pages/Leads'));
const BlogAdmin = lazyPage(() => import('./pages/BlogAdmin'));
const Partners = lazyPage(() => import('./pages/Partners'));
const DripConsole = lazyPage(() => import('./pages/DripConsole'));
import Widget from './pages/public/Widget';
const Compare = lazyPage(() => import('./pages/public/Compare'));
const CompareAll = lazyPage(() => import('./pages/public/CompareAll'));
const SwitchAnalysis = lazyPage(() => import('./pages/public/SwitchAnalysis'));
import TermPage from './site/TermPage';
import GoalPage from './site/GoalPage';
import VaultHub from './site/VaultHub';
import VaultArticle from './site/VaultArticle';
const WeeklyBrief = lazyPage(() => import('./pages/public/WeeklyBrief'));
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import TermsOfService from './pages/public/TermsOfService';
const RealtorBrief = lazyPage(() => import('./pages/RealtorBrief'));
const FeatureSheets = lazyPage(() => import('./pages/FeatureSheets'));
const FeatureSheetQueue = lazyPage(() => import('./pages/FeatureSheetQueue'));
const FeatureSheetPublic = lazyPage(() => import('./pages/public/FeatureSheetPublic'));
const RealtorSelfServe = lazyPage(() => import('./pages/public/RealtorSelfServe'));
import ProtectedRoute from './components/ProtectedRoute';
import { portalRoutes } from './portal/routes';
import { signingRoutes } from './signing/routes';
import AppShell from './components/AppShell';
import HomePage from './site/HomePage';
const DesignLabUncle = lazyPage(() => import('./site/DesignLabUncle'));
const DesignLabCarousel = lazyPage(() => import('./site/DesignLabCarousel'));
import ComingSoonPage from './site/ComingSoonPage';
import ContactPage from './site/ContactPage';
import ExperiencePage from './site/ExperiencePage';
import NotFoundPage from './site/NotFoundPage';
import StartPage from './site/StartPage';
import BookPage from './site/BookPage';
import BookedPage from './site/BookedPage';
import MattPage from './site/MattPage';
const StartAdmin = lazyPage(() => import('./pages/StartAdmin'));
const ClientAccounts = lazyPage(() => import('./pages/ClientAccounts'));
const MortgageApplicationsAdmin = lazyPage(() => import('./pages/MortgageApplicationsAdmin'));
const BookingsAdmin = lazyPage(() => import('./pages/BookingsAdmin'));
const ClientPortalPreview = lazyPage(() => import('./pages/ClientPortalPreview'));
const ClientPortalHome = lazyPage(() => import('./site/portal/ClientPortalHome'));
const ResetPasswordPage = lazyPage(() => import('./site/portal/ClientPortalHome'), 'ResetPasswordPage');
const VerifyEmailPage = lazyPage(() => import('./site/portal/ClientPortalHome'), 'VerifyEmailPage');
const WelcomePage = lazyPage(() => import('./site/portal/ClientPortalHome'), 'WelcomePage');
import { ClientAuthProvider } from './site/portal/ClientAuth';
const MortgageApplication = lazyPage(() => import('./site/portal/application/MortgageApplication'));
import { fthbRoutes } from './fthb/routes';
import { rcRoutes } from './rc/routes';
import { prRoutes } from './pr/routes';
import BookingDrawerHost from './site/booking/BookingDrawerHost';
const PrAdmin = lazyPage(() => import('./pr/pages/AdminPage'));
const RcAdmin = lazyPage(() => import('./rc/pages/AdminPage'));
const SwitchLinksAdmin = lazyPage(() => import('./pages/SwitchLinksAdmin'));
const BocCallsAdmin = lazyPage(() => import('./pages/BocCallsAdmin'));
const FthbAdmin = lazyPage(() => import('./fthb/pages/Admin'));
import LocationsPage from './site/LocationsPage';
import ListingsPage from './site/ListingsPage';
import TrackRecordPage from './site/TrackRecordPage';
import MunicipalPage from './site/MunicipalPage';
import SolutionPage from './site/SolutionPage';
import ClientLoginPage from './site/ClientLoginPage';
import PlaybooksHub from './site/PlaybooksHub';
import PlaybookPage from './site/PlaybookPage';
import ReviewsPage from './site/ReviewsPage';
import AfterClosingPage from './site/AfterClosingPage';
import CheckupPage from "./site/CheckupPage";
import RenewalReminderPage from "./site/RenewalReminderPage";
import SolutionsHubPage from "./site/SolutionsHubPage";
import HowItWorksPage from './site/HowItWorksPage';
import CityRatesPage from './site/CityRatesPage';
import CalculatorsHub from './site/calculators/CalculatorsHub';
import CalculatorPage from './site/calculators/CalculatorPage';
import CashFlowOptimizer from './site/calculators/CashFlowOptimizer';
import BudgetBoost from './site/calculators/BudgetBoost';
const MarketSnapshotsAdmin = lazyPage(() => import('./pages/MarketSnapshotsAdmin'));
const TestimonialsAdmin = lazyPage(() => import('./pages/TestimonialsAdmin'));
const CityRatesWidgetTool = lazyPage(() => import('./site/CityRatesWidgetTool'));
import { allSiteRoutes, ALBERTA_CITIES } from './site/nav';
import { TOOLS as CALC_TOOLS } from './site/calculators/tools';
import { REGIONS } from './site/cities';

const CITY_SLUGS = [...new Set([...ALBERTA_CITIES.map(([, h]) => h.replace(/\//g, '')), ...Object.values(REGIONS).flat()])];
const CALC_SLUGS = CALC_TOOLS.map((t) => t.slug);
function BlogRedirect() { const { slug } = useParams(); return <Navigate to={`/strategy-vault/${slug}/`} replace />; }

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

function AdminEntry() {
  // /admin → login when logged out, dashboard when logged in
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-zinc-50 text-zinc-500 flex items-center justify-center font-data text-xs tracking-[0.25em] uppercase">
      loading…
    </div>
  );
  return user ? <Navigate to="/admin/dashboard" replace /> : <Login />;
}

function AppRoutes() {
  const location = useLocation();
  useMetaPageView();
  // Intercept OAuth callback fragment (any route)
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  // One URL per page: public paths always end in "/" (sheet/token, admin, portal and file-like paths are left alone)
  const p = location.pathname;
  if (!p.endsWith('/') && !/\.[a-z0-9]{2,5}$/i.test(p) && !/^\/(sheet|api|admin|portal|switch|switch-analysis|options|strategy|renewal|s|weekly|blog)(\/|$)/.test(p)) {
    return <Navigate to={{ pathname: `${p}/`, search: location.search, hash: location.hash }} replace />;
  }
  return (
    <Routes>
      {/* Legacy URLs from the old site → permanent homes (JS redirects; Google follows them) */}
      <Route path="/index/" element={<Navigate to="/" replace />} />
      {["/ratewatch-plus/", "/ratewatch/"].map((p) => <Route key={p} path={p} element={<Navigate to="/after-closing/#ratewatch" replace />} />)}
      {['jasper', 'lac-la-biche', 'high-prairie'].map((c) => <Route key={c} path={`/${c}/`} element={<Navigate to="/locations/" replace />} />)}
      {/* Public client-facing widget — SEO indexable, no auth */}
      <Route path="/" element={<HomePage />} />
      <Route path="/rates/track-record" element={<TrackRecordPage />} />
      <Route path="/partner-portal/tools/city-rates-widget/" element={<CityRatesWidgetTool />} />
      <Route path="/rates" element={<Widget />} />
      <Route path="/rates-preview" element={<Navigate to="/rates" replace />} />
      <Route path="/home-preview" element={<Navigate to="/" replace />} />
      <Route path="/design-lab/uncle-posts/" element={<DesignLabUncle />} />
      <Route path="/design-lab/carousel/" element={<DesignLabCarousel />} />
      <Route path="/calculators/" element={<CalculatorsHub />} />
      <Route path="/calculators/cash-flow-optimizer/" element={<CashFlowOptimizer />} />
      <Route path="/calculators/budget-boost/" element={<BudgetBoost />} />
      <Route path="/calculators/switch-to-fixed/" element={<Navigate to="/rates/?intent=switch_to_fixed" replace />} />
      <Route path="/calculators/switch-to-fixed" element={<Navigate to="/rates/?intent=switch_to_fixed" replace />} />
      <Route path="/calculators/:slug/" element={<CalculatorPage />} />
      {CITY_SLUGS.map((c) => <Route key={c} path={`/${c}/`} element={<MunicipalPage />} />)}
      {/* City × solution pages (nested convention, approved 2026-09-10) — holding pages until the pattern is approved */}
      {CITY_SLUGS.map((c) => <Route key={`${c}-rates`} path={`/${c}/mortgage-rates/`} element={<CityRatesPage />} />)}
      <Route path="/:city/mortgage-rates/:slug" element={<TermPage />} />
      {CITY_SLUGS.map((c) => <Route key={`${c}-sub`} path={`/${c}/:sub/`} element={<SolutionPage />} />)}
      <Route path="/mortgages/:slug/" element={<SolutionPage />} />
      <Route path="/client-login/" element={<ClientLoginPage />} />
      <Route path="/portal/" element={<ClientPortalHome />} />
      <Route path="/portal/application/" element={<MortgageApplication />} />
      <Route path="/portal/reset/:token" element={<ResetPasswordPage />} />
      <Route path="/portal/welcome/:token" element={<WelcomePage />} />
      <Route path="/portal/verify/:token" element={<VerifyEmailPage />} />
      <Route path="/contact/" element={<ContactPage />} />
      <Route path="/closing-checklist/" element={<Navigate to="/strategy-vault/what-not-to-do-before-closing/" replace />} />
      <Route path="/book/" element={<BookPage />} />
      <Route path="/booked/" element={<BookedPage />} />
      <Route path="/why-hello/" element={<ExperiencePage />} />
      <Route path="/after-closing/" element={<AfterClosingPage />} />
      <Route path="/tools/yearly-mortgage-checkup/" element={<CheckupPage />} />
      <Route path="/tools/renewal-reminder/" element={<RenewalReminderPage />} />
      <Route path="/mortgages/" element={<SolutionsHubPage />} />
      <Route path="/how-it-works/" element={<HowItWorksPage />} />
      {[["ratewatch", "ratewatch"], ["renewal-defense", "renewal-defense"], ["homeowner-reports", "homeowner-reports"], ["annual-mortgage-review", "annual-review"]].map(([old, anchor]) => <Route key={old} path={`/why-hello/${old}/`} element={<Navigate to={`/after-closing/#${anchor}`} replace />} />)}
      <Route path="/why-hello/how-it-works/" element={<Navigate to="/how-it-works/" replace />} />
      <Route path="/about/" element={<Navigate to="/why-hello/#story" replace />} />
      <Route path="/locations/" element={<LocationsPage />} />
      <Route path="/listings/" element={<ListingsPage />} />
      {CITY_SLUGS.map((c) => <Route key={`${c}-fthb`} path={`/${c}/first-time-home-buyers/`} element={<SolutionPage />} />)}
      <Route path="/playbooks/" element={<PlaybooksHub />} />
      <Route path="/playbooks/:slug/" element={<PlaybookPage />} />
      <Route path="/why-hello/reviews/" element={<ReviewsPage />} />
      {allSiteRoutes().filter((p) => !p.includes('#') && !['/blog', '/privacy/', '/rates', '/client-login/', '/locations/', '/listings/', '/contact/', '/why-hello/'].includes(p) && !p.startsWith('/strategy-vault/') && !/^\/rates\/(\d-yr-|first-time-buyers|renewals|refinance)/.test(p) && !p.startsWith('/calculators/') && !p.startsWith('/playbooks/') && !p.startsWith('/tools/') && !p.startsWith('/first-home-plan/') && !p.startsWith('/renewal-checker/') && !p.startsWith('/payment-relief/') && !p.startsWith('/mortgages/') && !CITY_SLUGS.includes(p.replace(/\//g, ''))).map((p) => (
        <Route key={p} path={p} element={<ComingSoonPage />} />
      ))}
      <Route path="/privacy" element={<Navigate to="/privacy/" replace />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/compare-all" element={<CompareAll />} />
      <Route path="/switch-analysis" element={<SwitchAnalysis />} />
      <Route path="/switch-analysis/shared/:token" element={<SwitchAnalysis />} />
      <Route path="/switch/:ptoken" element={<SwitchAnalysis />} />
      {["first-time-buyers", "renewals", "refinance"].map((g) => <Route key={g} path={`/rates/${g}`} element={<GoalPage />} />)}
      <Route path="/rates/:slug" element={<TermPage />} />
      <Route path="/strategy-vault/" element={<VaultHub />} />
      <Route path="/strategy-vault/topic/:topic/" element={<VaultHub />} />
      <Route path="/strategy-vault/:slug/" element={<VaultArticle />} />
      <Route path="/blog" element={<Navigate to="/strategy-vault/" replace />} />
      <Route path="/blog/:slug" element={<BlogRedirect />} />
      <Route path="/weekly/:slug" element={<WeeklyBrief />} />
      <Route path="/privacy/" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/sheet/:token" element={<FeatureSheetPublic />} />
      <Route path="/sheet/:slug/:token" element={<FeatureSheetPublic />} />
      <Route path="/realtor" element={<RealtorSelfServe />} />
      <Route path="/realtor-sheet" element={<Navigate to="/realtor" replace />} />

      {/* Admin login (Google Auth) */}
      <Route path="/admin" element={<AdminEntry />} />

      {/* Admin app — auth-gated under /admin/* */}
      <Route path="/admin/dashboard" element={<ProtectedRoute><Shell><ModulesHome /></Shell></ProtectedRoute>} />
      <Route path="/admin/rates/dashboard" element={<ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>} />
      <Route path="/admin/rates/archive-audit" element={<ProtectedRoute><Shell><RateArchiveAudit /></Shell></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><Shell><AnalyticsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/redirects" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/rankings" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/actions" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/audit" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/optimize" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/seo/descriptions" element={<ProtectedRoute><Shell><SeoAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/rates" element={<ProtectedRoute><Shell><Rates /></Shell></ProtectedRoute>} />
      <Route path="/admin/promos" element={<ProtectedRoute><Shell><Promos /></Shell></ProtectedRoute>} />
      <Route path="/admin/policies" element={<ProtectedRoute><Shell><Policies /></Shell></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute><Shell><Documents /></Shell></ProtectedRoute>} />
      <Route path="/admin/buydown" element={<ProtectedRoute><Shell><Buydown /></Shell></ProtectedRoute>} />
      <Route path="/admin/history" element={<ProtectedRoute><Shell><RateHistory /></Shell></ProtectedRoute>} />
      <Route path="/admin/predictor" element={<ProtectedRoute><Shell><Predictor /></Shell></ProtectedRoute>} />
      <Route path="/admin/predictor/learning" element={<ProtectedRoute><Shell><PredictorLearning /></Shell></ProtectedRoute>} />
      <Route path="/admin/picker" element={<ProtectedRoute><Shell><LenderPicker /></Shell></ProtectedRoute>} />
      <Route path="/admin/featured-rates" element={<ProtectedRoute><Shell><FeaturedRatesAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/rates/public-review" element={<ProtectedRoute><Shell><PromoPublicReview /></Shell></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><Shell><Leads /></Shell></ProtectedRoute>} />
      <Route path="/admin/market-snapshots" element={<ProtectedRoute><Shell><MarketSnapshotsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/testimonials" element={<ProtectedRoute><Shell><TestimonialsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute><Shell><BlogAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/partners" element={<ProtectedRoute><Shell><Partners /></Shell></ProtectedRoute>} />
      <Route path="/admin/drip" element={<ProtectedRoute><Shell><DripConsole /></Shell></ProtectedRoute>} />
      <Route path="/admin/realtor-brief" element={<ProtectedRoute><Shell><RealtorBrief /></Shell></ProtectedRoute>} />
      <Route path="/admin/feature-sheets" element={<ProtectedRoute><Shell><FeatureSheets /></Shell></ProtectedRoute>} />
      <Route path="/admin/feature-sheets/queue" element={<ProtectedRoute><Shell><FeatureSheetQueue /></Shell></ProtectedRoute>} />
      <Route path="/admin/lenders/:id" element={<ProtectedRoute><Shell><LenderProfile /></Shell></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><Shell><Settings /></Shell></ProtectedRoute>} />
      <Route path="/admin/search" element={<ProtectedRoute><Shell><Search /></Shell></ProtectedRoute>} />

      {/* Legacy routes — redirect to admin equivalents */}
      <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/rates" element={<Navigate to="/admin/rates" replace />} />
      <Route path="/promos" element={<Navigate to="/admin/promos" replace />} />
      <Route path="/policies" element={<Navigate to="/admin/policies" replace />} />
      <Route path="/documents" element={<Navigate to="/admin/documents" replace />} />
      <Route path="/buydown" element={<Navigate to="/admin/buydown" replace />} />
      <Route path="/history" element={<Navigate to="/admin/history" replace />} />
      <Route path="/predictor" element={<Navigate to="/admin/predictor" replace />} />
      <Route path="/picker" element={<Navigate to="/admin/picker" replace />} />
      <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />
      <Route path="/search" element={<Navigate to="/admin/search" replace />} />

      {fthbRoutes()}
      {rcRoutes()}
      {prRoutes()}
      <Route path="/start" element={<StartPage />} />
      <Route path="/matt" element={<MattPage />} />
      <Route path="/start/" element={<StartPage />} />
      <Route path="/hello" element={<Navigate to="/start" replace />} />
      <Route path="/admin/start" element={<ProtectedRoute><Shell><StartAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/client-accounts" element={<ProtectedRoute><Shell><ClientAccounts /></Shell></ProtectedRoute>} />
      <Route path="/admin/bookings" element={<ProtectedRoute><Shell><BookingsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/mortgage-applications" element={<ProtectedRoute><Shell><MortgageApplicationsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/mortgage-applications/:applicationId" element={<ProtectedRoute><Shell><MortgageApplicationsAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/client-accounts/:accountId/portal" element={<ProtectedRoute><ClientPortalPreview /></ProtectedRoute>} />
      <Route path="/admin/first-home-plan" element={<ProtectedRoute><Shell><FthbAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/renewal-checker" element={<ProtectedRoute><Shell><RcAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/switch-links" element={<ProtectedRoute><Shell><SwitchLinksAdmin /></Shell></ProtectedRoute>} />
      <Route path="/admin/boc-calls" element={<ProtectedRoute><Shell><BocCallsAdmin /></Shell></ProtectedRoute>} />
      {portalRoutes}
      {signingRoutes}
      <Route path="/admin/payment-relief" element={<ProtectedRoute><Shell><PrAdmin /></Shell></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  useShellSeoCleanup();
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ClientAuthProvider>
              <AppRoutes />
              <BookingDrawerHost />
            </ClientAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
